(function () {
  class KiddoWonderCustomTale {
    constructor(root) {
      if (!root || root.dataset.taleInitialized === 'true') return;
      root.dataset.taleInitialized = 'true';

      this.root = root;
      this.engine = (root.dataset.engineUrl || '').replace(/\/+$/, '');
      this.variantId = String(root.dataset.variantId || '').trim();
      this.cartUrl = root.dataset.cartUrl || '/cart';
      this.formView = root.querySelector('[data-tale-form-view]');
      this.form = root.querySelector('[data-tale-form]');
      this.progressView = root.querySelector('[data-tale-progress]');
      this.previewView = root.querySelector('[data-tale-preview]');
      this.alert = root.querySelector('[data-tale-alert]');
      this.generateButton = root.querySelector('[data-generate-button]');
      this.progressStage = root.querySelector('[data-progress-stage]');
      this.progressBar = root.querySelector('[data-progress-bar]');
      this.photoInput = root.querySelector('[data-photo-input]');
      this.photoDropzone = root.querySelector('[data-photo-dropzone]');
      this.photoPreview = root.querySelector('[data-photo-preview]');
      this.photoReady = root.querySelector('[data-photo-ready]');
      this.progressTrack = root.querySelector('[data-progress-track]');
      this.batchId = '';
      this.previewData = null;
      this.pollTimer = null;
      this.pollFailures = 0;
      this.maxPollFailures = 4;
      this.progressValue = 0;
      this.photoObjectUrl = '';
      this.photoFile = null;
      this.clientId = '';
      this.batchStorageKey = 'kw_custom_tale_batch';

      this.bindEvents();
      this.restoreBatch();
    }

    bindEvents() {
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.photoInput.addEventListener('change', () => this.showPhoto());
      this.root.querySelector('[data-regenerate-button]').addEventListener('click', () => this.regenerate());
      this.root.querySelector('[data-edit-button]').addEventListener('click', () => this.edit());
      this.root.querySelector('[data-cart-button]').addEventListener('click', () => this.addToCart());

      ['dragenter', 'dragover'].forEach((name) => {
        this.photoDropzone.addEventListener(name, (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
          this.photoDropzone.classList.add('is-dragging');
        });
      });
      ['dragleave', 'dragend'].forEach((name) => {
        this.photoDropzone.addEventListener(name, () => this.photoDropzone.classList.remove('is-dragging'));
      });
      this.photoDropzone.addEventListener('drop', (event) => this.useDroppedPhoto(event));
    }

    apiUrl(path) {
      if (!this.engine) throw new Error('The Custom Tale engine URL is not configured.');
      return new URL(path.replace(/^\//, ''), `${this.engine}/`).toString();
    }

    async fetchJson(url, options) {
      let response;
      try {
        response = await fetch(url, options || {});
      } catch (cause) {
        const error = new Error('We could not reach the preview service. Please check your connection and try again.');
        error.retryable = true;
        error.cause = cause;
        throw error;
      }
      const data = await response.json().catch(() => null);
      if (!data || typeof data !== 'object') {
        const error = new Error(`The preview service returned an invalid response (${response.status}).`);
        error.retryable = response.ok || response.status >= 500;
        throw error;
      }
      if (!response.ok || data.error) {
        const error = new Error(data.error || data.message || `Request failed (${response.status})`);
        error.retryable = response.status === 408 || response.status === 425 || response.status === 429 || response.status >= 500;
        throw error;
      }
      return data;
    }

    useDroppedPhoto(event) {
      event.preventDefault();
      event.stopPropagation();
      this.photoDropzone.classList.remove('is-dragging');
      const droppedFiles = event.dataTransfer && event.dataTransfer.files;
      const file = droppedFiles && droppedFiles[0];
      if (!file) return;

      if (droppedFiles.length !== 1) {
        this.resetPhoto();
        this.markPhotoInvalid();
        this.showAlert('Please drop one child photo at a time.');
        this.photoInput.focus();
        return;
      }

      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        this.photoInput.files = transfer.files;
      } catch (error) {
        try {
          this.photoInput.files = droppedFiles;
        } catch (ignored) {
          // The explicit File stored below is sufficient for submission.
        }
      }

      // The stored File is also placed directly into FormData during submit,
      // so drag-and-drop still works when a browser blocks assigning input.files.
      this.showPhoto(file);
    }

    resetPhoto() {
      if (this.photoObjectUrl) URL.revokeObjectURL(this.photoObjectUrl);
      this.photoObjectUrl = '';
      this.photoFile = null;
      this.photoInput.value = '';
      this.photoPreview.onload = null;
      this.photoPreview.onerror = null;
      this.photoPreview.removeAttribute('src');
      this.photoPreview.hidden = true;
      this.photoDropzone.classList.remove('has-photo');
      this.photoReady.hidden = true;
    }

    markPhotoInvalid() {
      this.photoInput.setAttribute('aria-invalid', 'true');
      if (this.alert.id) this.photoInput.setAttribute('aria-errormessage', this.alert.id);
      this.photoDropzone.classList.add('is-invalid');
    }

    markPhotoValid() {
      this.photoInput.removeAttribute('aria-invalid');
      this.photoInput.removeAttribute('aria-errormessage');
      this.photoDropzone.classList.remove('is-invalid');
    }

    showPhoto(selectedFile) {
      const file = selectedFile || (this.photoInput.files && this.photoInput.files[0]);
      if (!file) return;
      if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
        this.resetPhoto();
        this.markPhotoInvalid();
        this.showAlert('Please choose a JPG, PNG, or WebP photo.');
        this.photoInput.focus();
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        this.resetPhoto();
        this.markPhotoInvalid();
        this.showAlert('The photo is too large. Please choose an image under 15 MB.');
        this.photoInput.focus();
        return;
      }
      if (this.photoObjectUrl) URL.revokeObjectURL(this.photoObjectUrl);
      this.photoFile = file;
      this.photoObjectUrl = URL.createObjectURL(file);
      this.photoPreview.hidden = true;
      this.photoDropzone.classList.remove('has-photo');
      this.photoReady.hidden = true;
      this.photoPreview.onload = () => {
        this.photoPreview.hidden = false;
        this.photoDropzone.classList.add('has-photo');
        this.photoReady.hidden = false;
      };
      this.photoPreview.onerror = () => {
        this.resetPhoto();
        this.markPhotoInvalid();
        this.showAlert('We could not read that photo. Please choose another JPG, PNG, or WebP image.');
      };
      this.photoPreview.src = this.photoObjectUrl;
      this.markPhotoValid();
      this.clearAlert();
    }

    scrollBehavior() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    showAlert(message) {
      this.alert.textContent = message || 'Something went wrong. Please try again.';
      this.alert.hidden = false;
      this.alert.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
    }

    clearAlert() {
      this.alert.hidden = true;
      this.alert.textContent = '';
    }

    setView(view) {
      this.formView.hidden = view !== 'form';
      this.progressView.hidden = view !== 'progress';
      this.previewView.hidden = view !== 'preview';
      this.root.setAttribute('aria-busy', view === 'progress' ? 'true' : 'false');
      if (view !== 'form') this.root.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
    }

    setProgress(progress, stage) {
      const safeProgress = Math.max(0, Math.min(100, Number(progress || 0)));
      this.progressValue = safeProgress;
      this.progressBar.style.width = `${safeProgress}%`;
      this.progressStage.textContent = stage || 'Creating your preview...';
      if (this.progressTrack) {
        this.progressTrack.setAttribute('aria-valuenow', String(Math.round(safeProgress)));
        this.progressTrack.setAttribute('aria-valuetext', `${Math.round(safeProgress)}% — ${stage || 'Creating your preview'}`);
      }
    }

    isValidBatch(batchId) {
      return /^[a-f0-9]{32}$/i.test(String(batchId || ''));
    }

    storeBatch(batchId) {
      try {
        if (batchId) window.sessionStorage.setItem(this.batchStorageKey, batchId);
        else window.sessionStorage.removeItem(this.batchStorageKey);
      } catch (error) {
        // Storage can be disabled in privacy modes; the current page still works.
      }
    }

    clearCustomBatchFromUrl() {
      const url = new URL(window.location.href);
      const batch = url.searchParams.get('batch');
      if (!this.isValidBatch(batch)) return;
      url.searchParams.delete('batch');
      window.history.replaceState({}, '', url.toString());
    }

    restoreBatch() {
      const urlBatch = new URL(window.location.href).searchParams.get('batch');
      let storedBatch = '';
      try {
        storedBatch = window.sessionStorage.getItem(this.batchStorageKey) || '';
      } catch (error) {
        storedBatch = '';
      }

      // Shopify's Theme Editor can use its own `batch` query parameter.
      // Custom Tale batches are always UUID4 hex values created by Render.
      const batch = this.isValidBatch(urlBatch) ? urlBatch : storedBatch;
      if (!this.isValidBatch(batch)) return;
      if (this.isValidBatch(urlBatch)) this.clearCustomBatchFromUrl();
      this.batchId = batch;
      this.storeBatch(batch);
      this.pollFailures = 0;
      this.setView('progress');
      this.setProgress(5, 'Restoring your Custom Tale preview...');
      this.poll();
    }

    async submit(event) {
      event.preventDefault();
      this.clearAlert();
      if (!this.photoFile) {
        this.markPhotoInvalid();
        this.showAlert('Please upload a child photo.');
        this.photoInput.focus();
        return;
      }
      if (!this.form.reportValidity()) return;

      this.generateButton.disabled = true;
      this.pollFailures = 0;
      this.setView('progress');
      this.setProgress(4, 'Uploading the photo securely...');

      try {
        const formData = new FormData(this.form);
        formData.set('reference', this.photoFile, this.photoFile.name || 'child-photo.jpg');
        const data = await this.fetchJson(this.apiUrl('/api/custom-tale/generate'), {
          method: 'POST',
          body: formData,
        });
        if (!this.isValidBatch(data.batch)) {
          const error = new Error('The preview service returned an invalid reference. Please try again.');
          error.retryable = false;
          throw error;
        }
        this.batchId = data.batch;
        this.storeBatch(this.batchId);
        this.setProgress(8, 'Writing a story from your idea...');
        this.poll();
      } catch (error) {
        this.generateButton.disabled = false;
        this.setView('form');
        this.showAlert(error.message);
      }
    }

    async poll() {
      window.clearTimeout(this.pollTimer);
      if (!this.batchId) return;
      const batch = this.batchId;
      try {
        const data = await this.fetchJson(this.apiUrl(`/api/custom-tale/${encodeURIComponent(batch)}`), {
          cache: 'no-store',
        });
        if (!batch || this.batchId !== batch) return;
        this.setProgress(data.progress, data.stage);
        if (data.status === 'error') {
          const error = new Error(data.error || 'Preview generation failed.');
          error.retryable = false;
          throw error;
        }
        if (data.ready || data.status === 'ready') {
          await this.loadPreview(batch);
          this.pollFailures = 0;
          return;
        }
        this.pollFailures = 0;
        this.pollTimer = window.setTimeout(() => this.poll(), 2500);
      } catch (error) {
        if (!batch || this.batchId !== batch) return;
        const canRetry = error.retryable !== false && this.pollFailures < this.maxPollFailures;
        if (canRetry) {
          this.pollFailures += 1;
          const retryDelay = Math.min(8000, 1500 * (2 ** (this.pollFailures - 1)));
          this.setProgress(
            this.progressValue,
            `Connection interrupted. Reconnecting (${this.pollFailures}/${this.maxPollFailures})...`
          );
          this.pollTimer = window.setTimeout(() => this.poll(), retryDelay);
          return;
        }
        if (error.retryable === false) this.storeBatch('');
        this.setView('form');
        this.generateButton.disabled = false;
        this.showAlert(error.message);
      }
    }

    async loadPreview(batch) {
      const data = await this.fetchJson(
        this.apiUrl(`/api/custom-tale/${encodeURIComponent(batch)}/preview`),
        { cache: 'no-store' }
      );
      if (!batch || this.batchId !== batch) return;
      if (!(data.cover && data.cover.image_url) || !(data.page_one && data.page_one.image_url)) {
        const error = new Error('Your preview images are still being prepared.');
        error.retryable = true;
        throw error;
      }
      this.previewData = data;
      await this.renderPreview(data);
      this.generateButton.disabled = false;
      this.setView('preview');
    }

    loadPreviewImage(image, url, alt) {
      return new Promise((resolve, reject) => {
        image.hidden = true;
        image.onload = () => {
          image.hidden = false;
          resolve();
        };
        image.onerror = () => {
          image.hidden = true;
          image.removeAttribute('src');
          const error = new Error('A preview image could not be loaded. Reconnecting...');
          error.retryable = true;
          reject(error);
        };
        image.alt = alt;
        image.src = url;
        if (image.complete && image.naturalWidth > 0) {
          image.hidden = false;
          resolve();
        }
      });
    }

    async renderPreview(data) {
      const childDetails = [data.child_name, data.child_age ? `age ${data.child_age}` : '', data.style_label]
        .filter(Boolean)
        .join(' · ');
      this.root.querySelector('[data-preview-title]').textContent = data.title || 'Your Custom Tale';
      this.root.querySelector('[data-preview-meta]').textContent = childDetails;
      const coverImage = this.root.querySelector('[data-cover-image]');
      const pageImage = this.root.querySelector('[data-page-image]');
      this.root.querySelector('[data-page-title]').textContent = data.page_one && data.page_one.title ? data.page_one.title : '';
      this.root.querySelector('[data-page-text]').textContent = data.page_one && data.page_one.text ? data.page_one.text : '';
      await Promise.all([
        this.loadPreviewImage(
          coverImage,
          data.cover.image_url,
          `${data.title || 'Custom Tale'} cover featuring ${data.child_name || 'the child'}`
        ),
        this.loadPreviewImage(
          pageImage,
          data.page_one.image_url,
          `First illustrated page featuring ${data.child_name || 'the child'}`
        ),
      ]);
    }

    async regenerate() {
      if (!this.batchId) return;
      this.clearAlert();
      const button = this.root.querySelector('[data-regenerate-button]');
      button.disabled = true;
      this.pollFailures = 0;
      this.setView('progress');
      this.setProgress(5, 'Regenerating the cover and first page...');
      try {
        await this.fetchJson(this.apiUrl(`/api/custom-tale/${encodeURIComponent(this.batchId)}/regenerate`), {
          method: 'POST',
        });
        button.disabled = false;
        this.poll();
      } catch (error) {
        button.disabled = false;
        this.setView('preview');
        this.showAlert(error.message);
      }
    }

    edit() {
      window.clearTimeout(this.pollTimer);
      this.storeBatch('');
      this.clearCustomBatchFromUrl();
      this.batchId = '';
      this.previewData = null;
      this.pollFailures = 0;
      this.clearAlert();
      this.setView('form');
    }

    getClientId() {
      if (this.clientId) return this.clientId;

      try {
        const existing = window.localStorage.getItem('kb_uid');
        const validExisting = /^[a-f0-9]{32}$/i.test(existing || '')
          || /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(existing || '');
        if (validExisting) {
          this.clientId = existing;
          return existing;
        }
      } catch (error) {
        // Continue with an in-memory ID when storage is unavailable.
      }

      const id = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID().replace(/-/g, '')
        : this.randomHexId();
      try {
        window.localStorage.setItem('kb_uid', id);
      } catch (error) {
        // Private browsing or strict settings may block localStorage.
      }
      this.clientId = id;
      return id;
    }

    randomHexId() {
      if (window.crypto && window.crypto.getRandomValues) {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
      }
      return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    }

    shopifyRoute(path) {
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      return `${root.replace(/\/?$/, '/')}${String(path || '').replace(/^\//, '')}`;
    }

    async ensureSingleCustomTale() {
      let response;
      try {
        response = await fetch(this.shopifyRoute('cart.js'), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
      } catch (error) {
        throw new Error('Shopify could not check your cart. Please try again.');
      }
      const cart = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(cart.description || cart.message || 'Shopify could not check your cart.');
      }
      if (!Array.isArray(cart.items)) {
        throw new Error('Shopify returned an invalid cart response. Please try again.');
      }

      const hasCustomTale = cart.items.some((item) => {
        const properties = item.properties || {};
        return String(item.variant_id || item.id) === this.variantId
          || item.handle === 'custom-tale'
          || properties['_Book Slug'] === 'custom-tale'
          || properties['Book Slug'] === 'custom-tale';
      });
      if (hasCustomTale) {
        throw new Error('Your cart already contains a Custom Tale. Please complete that order or remove it before adding another.');
      }
    }

    async addToCart() {
      if (!this.previewData || !this.batchId) {
        this.showAlert('Generate the preview before adding this book to your cart.');
        return;
      }
      if (!this.variantId || this.variantId === '0') {
        this.showAlert('The Custom Tale product has not been selected in the Shopify Theme Editor.');
        return;
      }

      const button = this.root.querySelector('[data-cart-button]');
      const editButton = this.root.querySelector('[data-edit-button]');
      const regenerateButton = this.root.querySelector('[data-regenerate-button]');
      button.disabled = true;
      editButton.disabled = true;
      regenerateButton.disabled = true;
      button.textContent = 'Adding to cart...';
      this.clearAlert();

      try {
        const clientId = this.getClientId();
        const properties = {
          'Child Name': this.previewData.child_name || '',
          'Child Age': this.previewData.child_age || '',
          'Child Gender': this.previewData.child_gender || '',
          'Language': this.previewData.language || '',
          '_Book Slug': 'custom-tale',
          '_Batch': this.batchId,
          '_KB UID': clientId,
        };

        // The paid-order webhook reads the private technical properties from
        // this exact line item. Avoid public cart attributes and order notes.
        await this.ensureSingleCustomTale();

        const response = await fetch(this.shopifyRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: this.variantId, quantity: 1, properties }],
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Shopify could not add the Custom Tale to the cart.');
        }
        const addedItems = Array.isArray(result.items) ? result.items : [];
        const addedTale = addedItems.find((item) => {
          const properties = item.properties || {};
          return String(item.variant_id || item.id) === this.variantId
            && properties['_Batch'] === this.batchId
            && Number(item.quantity) === 1;
        });
        if (!addedTale) {
          throw new Error('Shopify did not confirm the Custom Tale cart item. Please try again.');
        }
        window.location.assign(this.cartUrl);
      } catch (error) {
        button.disabled = false;
        editButton.disabled = false;
        regenerateButton.disabled = false;
        button.textContent = 'Add Custom Tale to cart';
        this.showAlert(error.message);
      }
    }
  }

  function initialize(scope) {
    const rootScope = scope || document;
    rootScope.querySelectorAll('[data-custom-tale-builder]').forEach((root) => {
      new KiddoWonderCustomTale(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document));
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
